import React, { useState, useRef, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, TextInput } from 'react-native';
import { useMutation } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { useNavigation } from '@react-navigation/native';
import { Mail } from 'lucide-react';
import { webColors, webBorderRadius, webShadows } from '../../../../constants/webStyles';
import { validateOTP, requestOTP } from '../../../../services/auth';
import { setLoggedIn } from '../../../../reducers/auth/user';
import api from '../../../../services/api';
import { useIsMobile } from '../../../../hooks/useIsMobile';

const StepVerify = ({ formData, updateFormData, goToStep, onComplete, isDoctor }) => {
  const dispatch = useDispatch();
  const navigation = useNavigation();
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const [error, setError] = useState('');
  const inputRefs = useRef([]);
  const isMobile = useIsMobile();

  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const resendMutation = useMutation(
    (data) => requestOTP(data),
    {
      onError: (err) => {
        const data = err.response?.data;
        if (data?.errors) {
          const messages = Object.values(data.errors).flat();
          setError(messages.join('\n'));
        } else {
          setError(data?.message || 'Failed to resend verification code');
        }
      },
      onSuccess: (res) => {
        updateFormData('nonce', res.data.data.nonce);
        setCanResend(false);
        setTimer(60);
        setError('');
      },
    }
  );

  const verifyMutation = useMutation(
    (data) => validateOTP(data),
    {
      onError: (err) => {
        const data = err.response?.data;
        if (data?.errors) {
          const messages = Object.values(data.errors).flat();
          setError(messages.join('\n'));
        } else {
          setError(data?.message || 'Invalid verification code');
        }
      },
      onSuccess: (res) => {
        if (formData.isActivation) {
          // Account already exists — activate and log in directly
          const { token, slug } = res.data.data;
          api.defaults.headers.common.Authorization = `Bearer ${token}`;
          dispatch(setLoggedIn({ token, slug, isDoctor }));
          navigation.reset({ index: 0, routes: [{ name: 'HomeScreen' }] });
        } else {
          // User already registered in StepLocation, just complete
          onComplete();
        }
      },
    }
  );

  const handleOtpChange = (value, index) => {
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError('');

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.nativeEvent.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleResend = () => {
    resendMutation.mutate({ email: formData.email });
  };

  const handleVerify = () => {
    const otpCode = otp.join('');
    if (otpCode.length !== 6) {
      setError('Please enter the 6-digit verification code');
      return;
    }

    verifyMutation.mutate({
      email: formData.email,
      otp: otpCode,
      nonce: formData.nonce,
    });
  };

  const isLoading = verifyMutation.isLoading || resendMutation.isLoading;
  const otpComplete = otp.every(digit => digit !== '');

  return (
    <View style={styles.container}>
      <View style={styles.stepHeader}>
        <View style={styles.stepIcon}>
          <Mail size={48} color={webColors.primary} />
        </View>
        <Text style={styles.stepTitle}>Email Verification</Text>
        <Text style={styles.otpEmail}>
          We have sent an email to{' '}
          <Text style={styles.emailHighlight}>{formData.email}</Text>
        </Text>
      </View>

      {error ? (
        <View style={styles.errorBox}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      ) : null}

      <View style={[styles.otpBoxes, isMobile && styles.otpBoxesMobile]}>
        {otp.map((digit, index) => (
          <TextInput
            key={index}
            ref={(ref) => (inputRefs.current[index] = ref)}
            style={[
              styles.otpInput,
              isMobile && styles.otpInputMobile,
              digit && styles.otpInputFilled,
            ]}
            value={digit}
            onChangeText={(value) => handleOtpChange(value, index)}
            onKeyPress={(e) => handleKeyDown(e, index)}
            keyboardType="number-pad"
            maxLength={1}
            editable={!isLoading}
            selectTextOnFocus
          />
        ))}
      </View>

      <Text style={styles.otpHint}>
        It may take a few seconds to receive. You may also check your spam folder for the OTP.
      </Text>

      <TouchableOpacity
        style={[styles.resendBtn, (!canResend || isLoading) && styles.resendBtnDisabled]}
        onPress={handleResend}
        disabled={!canResend || isLoading}
      >
        <Mail size={16} color="#374151" />
        <Text style={styles.resendBtnText}>Resend Email Verification</Text>
      </TouchableOpacity>

      {!canResend && (
        <Text style={styles.timerText}>Please wait for {timer} seconds</Text>
      )}

      <View style={styles.btnRow}>
        {!formData.isActivation && (
          <TouchableOpacity
            style={styles.btnBack}
            onPress={() => goToStep(4)}
            disabled={isLoading}
          >
            <Text style={styles.btnBackText}>Back</Text>
          </TouchableOpacity>
        )}
        <TouchableOpacity
          style={[styles.btnNext, (!otpComplete || isLoading) && styles.btnNextDisabled]}
          onPress={handleVerify}
          disabled={!otpComplete || isLoading}
        >
          <Text style={[styles.btnNextText, (!otpComplete || isLoading) && styles.btnNextTextDisabled]}>
            {isLoading ? 'Verifying...' : 'Verify'}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  stepHeader: {
    marginBottom: 32,
  },
  stepIcon: {
    marginBottom: 16,
  },
  stepTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 8,
  },
  otpEmail: {
    fontSize: 16,
    color: '#374151',
  },
  emailHighlight: {
    color: webColors.primary,
    fontWeight: '600',
  },
  errorBox: {
    backgroundColor: webColors.errorLight,
    padding: 12,
    borderRadius: webBorderRadius.small,
    marginBottom: 16,
  },
  errorText: {
    color: webColors.error,
    fontSize: 14,
  },
  otpBoxes: {
    flexDirection: 'row',
    gap: 12,
    justifyContent: 'center',
    marginBottom: 20,
  },
  otpInput: {
    width: 52,
    height: 60,
    backgroundColor: webColors.backgroundAlt,
    borderWidth: 2,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.medium,
    fontSize: 24,
    fontWeight: '700',
    textAlign: 'center',
    color: webColors.textPrimary,
  },
  otpBoxesMobile: {
    gap: 8,
  },
  otpInputMobile: {
    width: 40,
  },
  otpInputFilled: {
    borderColor: webColors.primary,
    backgroundColor: webColors.primaryLight,
  },
  otpHint: {
    textAlign: 'center',
    fontSize: 14,
    color: webColors.primary,
    marginBottom: 32,
    lineHeight: 22,
  },
  resendBtn: {
    flexDirection: 'row',
    backgroundColor: '#f3f4f6',
    paddingVertical: 14,
    borderRadius: webBorderRadius.medium,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginBottom: 12,
  },
  resendBtnDisabled: {
    opacity: 0.6,
  },
  resendBtnIcon: {
    fontSize: 16,
  },
  resendBtnText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#374151',
  },
  timerText: {
    textAlign: 'center',
    fontSize: 14,
    color: webColors.textMuted,
    marginBottom: 24,
  },
  btnRow: {
    flexDirection: 'row',
    gap: 16,
    marginTop: 8,
  },
  btnBack: {
    flex: 1,
    backgroundColor: '#f3f4f6',
    paddingVertical: 16,
    borderRadius: webBorderRadius.medium,
    alignItems: 'center',
  },
  btnBackText: {
    color: '#374151',
    fontSize: 16,
    fontWeight: '600',
  },
  btnNext: {
    flex: 2,
    backgroundColor: webColors.primary,
    paddingVertical: 16,
    borderRadius: webBorderRadius.medium,
    alignItems: 'center',
    ...webShadows.button,
  },
  btnNextDisabled: {
    opacity: 0.6,
  },
  btnNextText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  btnNextTextDisabled: {
    color: '#ffffff',
  },
});

export default StepVerify;
